import { useEffect, useRef, useState } from 'react';

import { useFadingHighlight } from '@/components/hooks/useFadingHighlight';

import { Condition } from './ApplicantConditions.types';
import { CreatorGroup, CreatorGroupKey } from './ApplicantConditions.utils';

const NOTE_MOUNT_MAX_FRAMES = 10;

export interface FocusIntent {
    conditionId: string;
    target: 'row' | 'noteBox';
}

interface UseConditionFocusOptions {
    applicantId: string;
    openConditions: Condition[];
    closedConditions: Condition[];
    deletedConditions: Condition[];
    openGroups: CreatorGroup[];
    findConditionById: (conditionId: string) => Condition | null;
    focusConditionId: string | null;
    focusNoteId: string | null;
    onConditionFocused?: () => void;
}

export const useConditionFocus = ({
    applicantId,
    openConditions,
    closedConditions,
    deletedConditions,
    openGroups,
    findConditionById,
    focusConditionId,
    focusNoteId,
    onConditionFocused,
}: UseConditionFocusOptions) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [highlightedId, setHighlightedId] = useFadingHighlight();
    const [highlightedNoteId, setHighlightedNoteId] = useFadingHighlight();
    const [expandedConditionId, setExpandedConditionId] = useState<string | null>(null);
    const [isOpenExpanded, setIsOpenExpanded] = useState(true);
    const [isClosedExpanded, setIsClosedExpanded] = useState(false);
    const [isDeletedExpanded, setIsDeletedExpanded] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<CreatorGroupKey>>(new Set());

    // The applicant page isn't keyed by applicant, so this state would otherwise carry over.
    useEffect(() => {
        setIsOpenExpanded(true);
        setIsClosedExpanded(false);
        setIsDeletedExpanded(false);
        setCollapsedGroups(new Set());
    }, [applicantId]);

    const setGroupCollapsed = (key: CreatorGroupKey, collapsed: boolean) =>
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (collapsed) next.add(key);
            else next.delete(key);
            return next;
        });

    // Internal cross-link jumps reuse the deep-link scroll/highlight path.
    const [focusIntent, setFocusIntent] = useState<FocusIntent | null>(null);
    const focusTargetId = focusConditionId ?? focusIntent?.conditionId ?? null;
    const isNoteBoxTarget = !focusConditionId && focusIntent?.target === 'noteBox';
    const isFocusIn = (section: Condition[]) => section.some((c) => c.id === focusTargetId);

    // Derived at render, not set from the effect: the scroll lookup below needs the rows mounted on this commit.
    const isOpenSectionOpen = isOpenExpanded || isFocusIn(openConditions);
    const isClosedOpen = isClosedExpanded || isFocusIn(closedConditions);
    const isDeletedOpen =
        deletedConditions.length > 0 && (isDeletedExpanded || isFocusIn(deletedConditions));
    const isGroupOpen = (key: CreatorGroupKey, conditions: Condition[]) =>
        !collapsedGroups.has(key) || isFocusIn(conditions);

    useEffect(() => {
        if (!focusTargetId) return;
        setExpandedConditionId(focusTargetId);
        // Latch the section open so it stays open once the focus param clears.
        if (isFocusIn(openConditions)) setIsOpenExpanded(true);
        if (isFocusIn(closedConditions)) setIsClosedExpanded(true);
        if (isFocusIn(deletedConditions)) setIsDeletedExpanded(true);
        openGroups.forEach((g) => {
            if (isFocusIn(g.conditions)) setGroupCollapsed(g.key, false);
        });

        const focusedCondition = findConditionById(focusTargetId);
        if (!focusedCondition) return;
        const hasTargetNote = Boolean(
            focusNoteId && focusedCondition.notes.some((note) => note.id === focusNoteId),
        );
        if (hasTargetNote) setHighlightedNoteId(focusNoteId);
        else setHighlightedId(focusTargetId);

        // Defer the scroll across two frames: the design-system Tabs flips its
        // selected tab in an effect, so on the first commit this content is still
        // display:none and scrollIntoView would silently no-op. One frame for the
        // tab to become visible, one for the accordion expansion to lay out.
        let raf = 0;
        let framesLeft = NOTE_MOUNT_MAX_FRAMES;
        const scrollToTarget = () => {
            const conditionNode = containerRef.current?.querySelector(
                `[data-condition-id="${focusTargetId}"]`,
            );
            if (!conditionNode) return;
            const noteNode = hasTargetNote
                ? conditionNode.querySelector(`[data-condition-note-id="${focusNoteId}"]`)
                : null;
            if (hasTargetNote && !noteNode && framesLeft > 0) {
                framesLeft -= 1;
                raf = requestAnimationFrame(scrollToTarget);
                return;
            }
            const noteBox = isNoteBoxTarget
                ? conditionNode.querySelector<HTMLElement>(`[id="add-note-${focusTargetId}"]`)
                : null;
            if (isNoteBoxTarget && !noteBox && framesLeft > 0) {
                framesLeft -= 1;
                raf = requestAnimationFrame(scrollToTarget);
                return;
            }
            const scrollTarget = noteBox ?? noteNode ?? conditionNode;
            scrollTarget.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
            // An open dialog owns focus, so the note box must not take it behind the dialog.
            if (!document.querySelector('.p-dialog-mask')) noteBox?.focus({ preventScroll: true });
            // Only clear the param once we've actually located and scrolled to
            // the row — otherwise a not-yet-loaded condition would never retry.
            // A URL deep link takes priority, so the internal intent waits until the link clears.
            if (!focusConditionId) setFocusIntent(null);
            onConditionFocused?.();
        };
        raf = requestAnimationFrame(() => {
            raf = requestAnimationFrame(scrollToTarget);
        });
        return () => cancelAnimationFrame(raf);
    }, [
        focusTargetId,
        focusConditionId,
        isNoteBoxTarget,
        focusNoteId,
        openConditions,
        closedConditions,
        deletedConditions,
        onConditionFocused,
    ]);

    return {
        containerRef,
        highlightedId,
        highlightedNoteId,
        expandedConditionId,
        isOpenSectionOpen,
        isClosedOpen,
        isDeletedOpen,
        isGroupOpen,
        setIsOpenExpanded,
        setIsClosedExpanded,
        setIsDeletedExpanded,
        setGroupCollapsed,
        setFocusIntent,
    };
};
